import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./Profile.module.css";
import { TextField } from "@mui/material";
import CustomSelector from "../../../components/custom/CustomSelector";
import useApi from "../../../custom_hooks/useApi";
import {
  fetchReasonsApi,
  getAiProfileDescriptionApi,
  getProfileNewApi,
  updateProfileApi,
} from "../../../services/newServices";
import CustomButton from "../../../components/custom/CustomButton";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { uploadImagesToS3 } from "../../../utills/utils";
import LoaderComponent from "../../../components/custom/CustomLoader";
import UpdateProfile from "./UpdateProfile";
import CustomImageUpload from "../../../components/custom/CustomImageUpload";
import { trackEvent } from "../../../analytics";
import { useTranslation } from "react-i18next";
import storageService from "../../../services/storageServices";

export default function Profile() {
  const [fullName, setFullName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedLanguages, setSelectedLanguages] = useState({});
  const [selectedPersonalityTraits, setSelectedPersonalityTraits] = useState(
    {}
  );
  const [selectedProfileInterests, setSelectedProfileInterests] = useState({});
  const [image, setImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [submitLoading, setSubmitLoading] = useState([]);
  const [descriptionLoading, setDescriptionLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const imageRef = useRef();
  const maxLimit = 5;

  const {
    selectedHotel: { id: hotelId },
  } = useSelector((state) => state?.hotels);

  const ln = storageService.get("lang") || "en";

  const [profileData, profileDataLoading, profileDataError, profileDataFetch] =
    useApi(
      {
        api: getProfileNewApi,
        values: hotelId,
        doFetch: !!hotelId,
        defaultLoading: true,
      },
      [hotelId]
    );

  useEffect(() => {
    if (profileData) {
      setFullName(profileData.name ?? "");
      setDescription(profileData.description ?? "");
      setImage(profileData.imageUrl ?? null);
      setSelectedLanguages(
        profileData.languages?.reduce((acc, curr) => {
          acc[curr] = true;
          return acc;
        }, {}) ?? {}
      );
      setSelectedPersonalityTraits(
        profileData.personalityTraits?.reduce((acc, curr) => {
          acc[curr] = true;
          return acc;
        }, {}) ?? {}
      );
      setSelectedProfileInterests(
        profileData.profileInterests?.reduce((acc, curr) => {
          acc[curr] = true;
          return acc;
        }, {}) ?? {}
      );
       
    }
  }, [profileData]);

  const [languages] = useApi({
    api: fetchReasonsApi,
    values: `languages&ln=${ln}`,
    defaultValue: [],
    trackingName: "fetchReasonsApi",
  });

  const [personalityTraits] = useApi({
    api: fetchReasonsApi,
    values: `personality_traits&ln=${ln}`,
    defaultValue: [],
    trackingName: "fetchReasonsApi",
  });

  const [profileInterests] = useApi({
    api: fetchReasonsApi,
    values: `profile_interests&ln=${ln}`,
    defaultValue: [],
    trackingName: "fetchReasonsApi",
  });

  const handleLanguageSelect = (languageId) => {
    setSelectedLanguages({
      ...selectedLanguages,
      [languageId]: !selectedLanguages[languageId],
    });
  };

  const handlePersonalityTraitsSelect = (personalityTraitsId) => {
    setSelectedPersonalityTraits({
      ...selectedPersonalityTraits,
      [personalityTraitsId]: !selectedPersonalityTraits[personalityTraitsId],
    });
  };

  const handleProfileInterestsSelect = (profileInterestsId) => {
    setSelectedProfileInterests({
      ...selectedProfileInterests,
      [profileInterestsId]: !selectedProfileInterests[profileInterestsId],
    });
  };

  const selectedLanguagesCount = useMemo(
    () => Object.values(selectedLanguages).filter((val) => val).length,
    [selectedLanguages]
  );

  const selectedPersonalityTraitsCount = useMemo(
    () => Object.values(selectedPersonalityTraits).filter((val) => val).length,
    [selectedPersonalityTraits]
  );

  const selectedProfileInterestsCount = useMemo(
    () => Object.values(selectedProfileInterests).filter((val) => val).length,
    [selectedProfileInterests]
  );

  const getSelectedValues = (values) => {
    const mappedValues = Object.entries(values)
      .filter(([_, val]) => val)
      .map(([id]) => id);
    return mappedValues.length ? mappedValues : null;
  };

  const lang = storageService.get("lang") || "en";

  const handleNext = async () => {
    setCurrentPage((prev) => prev + 1);
    trackEvent("profile-next-button-clicked", { page: currentPage });
    let imageUrl;
    if (image && image !== profileData.imageUrl) {
      setImageLoading(true);
      const { data: url } = await uploadImagesToS3([image]);
      imageUrl = url[0];
      setImageLoading(false);
      trackEvent("profile-image-uploaded-to-s3", { imageUrl });
    }
    submitLoading[currentPage] = true;
    setSubmitLoading([...submitLoading]);
    updateProfileApi({
      hotelId,
      name: fullName,
      languages: getSelectedValues(selectedLanguages),
      personalityTraits: getSelectedValues(selectedPersonalityTraits),
      profileInterests: getSelectedValues(selectedProfileInterests),
      imageUrl: imageUrl || null,
      description: description || null,
    }).finally(() => {
      submitLoading[currentPage] = false;
      setSubmitLoading([...submitLoading]);
    });
    if (currentPage === 3) {
      setDescriptionLoading(true);
      getAiProfileDescriptionApi(hotelId, lang)
        .then((res) => {
          setDescription(res.data);
        })
        .finally(() => {
          setDescriptionLoading(false);
        });
      trackEvent("profile-ai-description-generation");
    }
  };

  const handleBack = () => {
    setCurrentPage((prev) => prev - 1);
    trackEvent("profile-back-button-clicked", { page: currentPage });
  };

  const handleView = () => {
    profileDataFetch();
    trackEvent("profile-view-button-clicked");
  };

  useEffect(() => {
    trackEvent("profile-page-visited");
  }, []);

  const { t } = useTranslation();

  return profileDataLoading ? (
    <LoaderComponent isLoading={true} />
  ) :profileData && Object.keys(profileData).length ? (
    <UpdateProfile profileData={profileData} />
  ) : (
    <div className={styles.container}>
      <div className={styles.title}>{t("about_you")}</div>
      <div className={styles["background-one"]}></div>
      <div className={styles["background-two"]}></div>
      <div
        className={styles.wrappers}
        style={{ top: `-${currentPage * 100}%` }}
      >
        <div className={styles.wrapper}>
          <div className={styles.text}>{t("your_name")}</div>
          <TextField
            label={t("full_name")}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className={styles.input}
          />
        </div>
        <div className={styles.wrapper}>
          <div className={styles.text}>
            {t("languages_you_know")}{" "}
            <span className={styles["count"]}>
              {selectedLanguagesCount}/{maxLimit}
            </span>
          </div>
          <div className={styles["selectors"]}>
            {languages.map((language) => {
              return (
                <CustomSelector
                  isActive={!!selectedLanguages[language.id]}
                  onSelect={() => handleLanguageSelect(language.id)}
                  key={language.id}
                  disable={
                    selectedLanguagesCount === maxLimit &&
                    !selectedLanguages[language.id]
                  }
                >
                  {language.description}
                </CustomSelector>
              );
            })}
          </div>
        </div>
        <div className={styles.wrapper}>
          <div className={styles.text}>
            {t("what_do_you_like")}{" "}
            <span className={styles["count"]}>
              {selectedPersonalityTraitsCount}/{maxLimit}
            </span>
          </div>
          <div className={styles["selectors"]}>
            {personalityTraits.map((personalityTraits) => {
              return (
                <CustomSelector
                  isActive={!!selectedPersonalityTraits[personalityTraits.id]}
                  onSelect={() =>
                    handlePersonalityTraitsSelect(personalityTraits.id)
                  }
                  key={personalityTraits.id}
                  disable={
                    selectedPersonalityTraitsCount === maxLimit &&
                    !selectedPersonalityTraits[personalityTraits.id]
                  }
                >
                  {personalityTraits?.Transalations?.name ||
                    personalityTraits.description}
                </CustomSelector>
              );
            })}
          </div>
        </div>
        <div className={styles.wrapper}>
          <div className={styles.text}>
            {t("what_is_your_nature")}{" "}
            <span className={styles["count"]}>
              {selectedProfileInterestsCount}/{maxLimit}
            </span>
          </div>
          <div className={styles["selectors"]}>
            {profileInterests.map((profileInterest) => {
              return (
                <CustomSelector
                  isActive={!!selectedProfileInterests[profileInterest.id]}
                  onSelect={() =>
                    handleProfileInterestsSelect(profileInterest.id)
                  }
                  key={profileInterest.id}
                  disable={
                    selectedProfileInterestsCount === maxLimit &&
                    !selectedProfileInterests[profileInterest.id]
                  }
                >
                  {profileInterest?.Transalations?.name ||
                    profileInterest.description}
                </CustomSelector>
              );
            })}
          </div>
        </div>
        <div className={styles.wrapper}>
          <div className={styles["image-wrapper"]}>
            <div className={styles.text}>{t("upload_an_image")}</div>
            <CustomImageUpload
              value={image}
              onChange={(img) => setImage(img)}
            />
          </div>
        </div>
        <div className={styles.wrapper}>
          <div className={styles.text}>{t("profile_completed")}</div>
          <div>{t("profile_completed_message")}</div>
        </div>
      </div>
      <div className={styles["buttons-container"]}>
        <CustomButton
          color="white"
          onClick={handleBack}
          style={{ border: "1px solid black", borderRadius: "5px" }}
          disable={currentPage === 0}
        >
          {t("back")}
        </CustomButton>
        {currentPage < 5 ? (
          <CustomButton
            color="primary"
            onClick={handleNext}
            loading={currentPage === 4 && (descriptionLoading || imageLoading)}
          >
            {t("next")}
          </CustomButton>
        ) : (
          <CustomButton color="primary" onClick={handleView}>
            {t("view_button")}
          </CustomButton>
        )}
      </div>
    </div>
  );
}
